<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { RouterLink } from 'vue-router'

import AppIcon from '@/components/common/AppIcon.vue'
import TribeIcon from '@/components/common/TribeIcon.vue'
import { useServerSelectionStore } from '@/stores/serverSelection'
import { useUserStore } from '@/stores/user'
import { sidebarPlayer } from '@/utils/sidebarPlayer'

/**
 * "Who am I playing right now": the character of the current group or server,
 * with its world, at the top of the sidebar. Two links — character and world —
 * to their detail pages. Nothing is drawn when there is no character to show.
 *
 * Collapsed to the icon rail it becomes two stacked targets (crest, globe),
 * each with its tooltip, since the rail has no room for text.
 */
const props = withDefaults(defineProps<{ collapsed?: boolean }>(), { collapsed: false })

const { t, locale } = useI18n()
const user = useUserStore()
const serverSelection = useServerSelectionStore()

const context = computed(() =>
  sidebarPlayer(serverSelection.selected, user.selectedGroup, user.playerOnServer),
)

const number = (value: number | null) =>
  value === null ? null : new Intl.NumberFormat(locale.value).format(value)

const stats = computed(() => {
  const player = context.value?.player
  if (!player) return []
  return [
    {
      key: 'rank',
      icon: 'mdi-trophy-outline',
      value: player.rank === null ? null : `#${number(player.rank)}`,
    },
    { key: 'population', icon: 'mdi-account-multiple-outline', value: number(player.population) },
    { key: 'villages', icon: 'mdi-home-group', value: number(player.villages) },
  ].filter((stat) => stat.value !== null)
})

const playerLabel = computed(() =>
  context.value ? t('sidebar.player.openPlayer', { name: context.value.player.alias }) : '',
)
const serverLabel = computed(() =>
  context.value ? t('sidebar.player.openServer', { name: context.value.server.name }) : '',
)
</script>

<template>
  <section
    v-if="context"
    class="shrink-0 border-b border-white/10 p-2"
    :aria-label="t('sidebar.player.label')"
  >
    <!-- Icon rail: crest and globe stacked, the text lives in the tooltips. -->
    <div v-if="props.collapsed" class="flex flex-col items-center gap-1">
      <RouterLink
        :to="{ name: 'player-detail', params: { id: context.player.id } }"
        class="bg-parchment-100 ring-gold-500/70 hover:ring-gold-300 flex h-10 w-10 items-center justify-center rounded-full ring-2 transition-shadow"
        :title="`${context.player.alias} · ${context.server.name}`"
        :aria-label="playerLabel"
      >
        <TribeIcon :icon="context.player.tribe?.icon" size-class="h-7 w-7" />
      </RouterLink>
      <RouterLink
        :to="{ name: 'server-detail', params: { id: context.server.id } }"
        class="chrome-item h-10 w-10 justify-center px-0"
        :title="context.server.name"
        :aria-label="serverLabel"
      >
        <AppIcon name="mdi-earth" class="text-lg text-sky-400" />
      </RouterLink>
    </div>

    <div v-else class="rounded-lg border border-white/10 bg-white/[0.04] p-1">
      <RouterLink
        :to="{ name: 'player-detail', params: { id: context.player.id } }"
        class="group flex items-center gap-3 rounded-md p-2 transition-colors hover:bg-white/10"
        :aria-label="playerLabel"
      >
        <span
          class="bg-parchment-100 ring-gold-500/70 group-hover:ring-gold-300 flex h-10 w-10 shrink-0 items-center justify-center rounded-full ring-2 transition-shadow"
        >
          <TribeIcon
            :icon="context.player.tribe?.icon"
            :name="context.player.tribe?.name"
            size-class="h-7 w-7"
          />
        </span>
        <span class="min-w-0 flex-1">
          <span class="flex min-w-0 items-center gap-1.5">
            <span class="font-display text-gold-300 truncate text-sm font-bold">
              {{ context.player.alias }}
            </span>
            <span
              v-if="context.player.alliance"
              class="badge text-parchment-100 shrink-0 bg-white/10 px-1.5 py-0 text-[11px]"
            >
              {{ context.player.alliance.tag_ally }}
            </span>
          </span>
          <span
            v-if="stats.length > 0"
            class="text-parchment-200/70 mt-0.5 flex items-center gap-2.5 text-[11px] tabular-nums"
          >
            <span
              v-for="stat in stats"
              :key="stat.key"
              class="flex items-center gap-0.5"
              :title="t(`sidebar.player.${stat.key}`)"
            >
              <AppIcon :name="stat.icon" class="text-xs" />
              <span class="sr-only">{{ t(`sidebar.player.${stat.key}`) }}:</span>
              {{ stat.value }}
            </span>
          </span>
        </span>
        <AppIcon
          name="mdi-chevron-right"
          class="text-parchment-200 text-base opacity-40 transition-opacity group-hover:opacity-100"
        />
      </RouterLink>

      <RouterLink
        :to="{ name: 'server-detail', params: { id: context.server.id } }"
        class="group text-parchment-200 flex min-h-10 items-center gap-2 rounded-md px-2 text-xs transition-colors hover:bg-white/10 hover:text-white"
        :aria-label="serverLabel"
      >
        <AppIcon name="mdi-earth" class="text-base text-sky-400" />
        <span class="min-w-0 flex-1 truncate">{{ context.server.name }}</span>
        <AppIcon
          name="mdi-chevron-right"
          class="text-base opacity-40 transition-opacity group-hover:opacity-100"
        />
      </RouterLink>
    </div>
  </section>
</template>
